import { CommonModule } from '@angular/common';
import { formatInTimeZone } from "date-fns-tz";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, MenuController, ModalController } from '@ionic/angular';
import { FacilityModel } from 'src/app/shared/models/facility.model';
import { StorageEnum } from '../../enums/storage.enum';
import { StorageService } from 'src/app/core/services/storage.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { UiUtilityService } from 'src/app/core/services/ui-utility.service';
import { Subscription, interval } from 'rxjs';
import { FacilityService } from 'src/app/features/settings/facility-management/services/facility.service';
import { MessageDrawerComponent } from '../message-drawer/message-drawer.component';
import { MESSAGES_REFRESH_INTERVAL } from '../../config/report.constant';
import { DashboardService } from 'src/app/features/dashboard/services/dashboard.service';
import { MSG_PARTS } from '../../config/app.constant';

@Component({
  standalone: true,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [IonicModule, CommonModule, FormsModule],
})
export class HeaderComponent implements OnInit, OnDestroy {
  availableFacilities: any[] = [];
  selectedFacility: FacilityModel = new FacilityModel();
  @Output() onOpenSummary = new EventEmitter<any>();
  isSettingModalOpen: boolean = false;
  isMenuOpen: boolean = false;
  private subscriptions: Subscription[] = [];
  private pccSubscription: Subscription | undefined;
  isPCCEnabled = false;
  messages: any = [];
  nonClearableList: any = [];
  lastUpdatedPCC = '';
  messageIcon = 'messages.svg';
  @Input('header') header = '';


  constructor(
    private authService: AuthService,
    private storageService: StorageService,
    private headerService: HeaderService,
    private menuCtrl: MenuController,
    private uiUtilityService: UiUtilityService,
    private facilityService: FacilityService,
    private modalCtrl: ModalController,
    private dashboardService: DashboardService
  ) { }

  ngOnInit() {
    this.isPCCEnabled = this.headerService.isPCCEnabled;
    const sortedFacilities = this.uiUtilityService.sortList(this.authService.getLoginData?.facility, 'facilityName');
    this.availableFacilities = sortedFacilities;
    if (this.availableFacilities?.length) {
      if (this.headerService.getCurrentFacility?.facilityId) {
        this.selectedFacility = this.headerService.getCurrentFacility;
      } else {
        this.selectedFacility = this.availableFacilities[0];
      }
      this.onFacilityChange();
    }
    this.subscriptions.push(
      this.uiUtilityService.isPageModalOpen.subscribe((isOpen: boolean) => {
        this.isSettingModalOpen = isOpen;
      })
    );
    this.subscriptions.push(
      this.uiUtilityService.isMenuOpen.subscribe((isOpen: boolean) => {
        this.isMenuOpen = isOpen;
      })
    );
    this.subscriptions.push(
      this.headerService.onGetMessagesData.subscribe(() => {
        this.getMessagesData();
      })
    );
    this.facilityService.facilityData.subscribe((isCurrent = false) => {
      const sortedFacilities = this.uiUtilityService.sortList(this.authService.getLoginData?.facility, 'facilityName');
      this.availableFacilities = sortedFacilities;
      if (isCurrent) {
        const fac = this.headerService.getCurrentFacility;
        if (fac.facilityId) {
          this.selectedFacility = fac;
          this.willGetMessageData();
        }
      }
    });
  }

  willGetMessageData() {
    this.pccSubscription?.unsubscribe();
    this.messages = [];
    if (this.header === 'main') {
      this.getMessagesData();
      this.pccSubscription = interval(MESSAGES_REFRESH_INTERVAL).subscribe(() => {
        this.getMessagesData();
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
    this.pccSubscription?.unsubscribe();
  }

  compareWith(o1: FacilityModel, o2: FacilityModel) {
    return o1 && o2 ? o1.facilityId === o2.facilityId : o1 === o2;
  }

  onFacilityChange() {
    this.storageService.setLocalStorage(
      StorageEnum.selectedFacility,
      this.selectedFacility,
      true
    );
    this.storageService.removeSessionStorage(StorageEnum.filteredGroups);
    this.headerService.changeFacility(this.selectedFacility);
    this.willGetMessageData();
  }

  toggleMenu() {
    this.menuCtrl.isOpen().then((isOpen) => {
      if (isOpen) {
        this.isMenuOpen = false;
        this.dismissNavModal();
      } else {
        this.isMenuOpen = true;
        this.menuCtrl.open();
      }
    });
  }

  dismissNavModal() {
    if (this.uiUtilityService.pageModal) {
      this.uiUtilityService.pageModal
        .dismiss()
        .then(() => this.menuCtrl.close());
      this.uiUtilityService.pageModal = null;
    } else {
      this.menuCtrl.close();
    }
  }

  openSummary() {
    this.onOpenSummary.emit();
  }

  formatMessages(list: any[]) {
    for (const res of list) {
      res.wearerName = this.uiUtilityService.getEntityShortDisplayName(res);
      res.parts = res.messageText.split(/(\{.*?\})/).filter((part: string) => part !== '');
      const formattedSubText = res.subText.replace(/{(.*?)}/g, (_: any, key: string) => res[key] ?? `{${key}}`);
      res.subParts = formattedSubText.split(/(\{.*?\})/).filter((part: string) => part !== '');
      res.partsData = { [MSG_PARTS.RESIDENT_ID]: res.wearerName, [MSG_PARTS.ROOM_NAME]: res.roomName };
    }
  }

  getMessagesData() {
    this.dashboardService.getPccNotificationForUser().subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          this.nonClearableList = response.data?.nonClearableList;
          const alertTime = response.data?.lastContactedPCCTime || '';
          if (alertTime) {
            this.lastUpdatedPCC = formatInTimeZone(new Date(alertTime * 1000), this.selectedFacility.timeZone, 'M/d/yyyy h:mm a');
          }
          const adtLogList = response.data?.adtLogList || [];
          this.formatMessages(adtLogList);
          const nonClearableList = response.data?.nonClearableList || [];
          this.formatMessages(nonClearableList);
          this.didGetMessagesData(adtLogList);
        }
      }, error: () => {

      }
    });
  }

  didGetMessagesData(response: any = []) {
    const notify = this.storageService.getLocalStorage(StorageEnum.notifications, true) || {};

    // ✅ Merge lists and remove duplicates by ID
    const merged = this.nonClearableList?.length
      ? [...response, ...this.nonClearableList].filter(
        (msg, i, arr) => i === arr.findIndex(x => x.id === msg.id)
      )
      : response;

    // ✅ Sort: newest first (by EVENTTIMESECS desc, then id desc)
    const msgs = merged.sort((a: any, b: any) => {
      if (a.EVENTTIMESECS === b.EVENTTIMESECS) {
        return b.id - a.id;
      }
      return b.EVENTTIMESECS - a.EVENTTIMESECS;
    });

    // ✅ Handle cleared notifications
    if (notify.cleared) {
      const index = msgs.findIndex((obj: any) => obj.id === notify.cleared);
      if (index !== -1) {
        const filteredMsgs = msgs.slice(0, index);
        this.messages = [...filteredMsgs, ...this.nonClearableList]
          .filter((msg, i, arr) => i === arr.findIndex(x => x.id === msg.id))
          .sort((a, b) => {
            if (a.EVENTTIMESECS === b.EVENTTIMESECS) {
              return b.id - a.id;
            }
            return b.EVENTTIMESECS - a.EVENTTIMESECS;
          });
      } else {
        this.messages = msgs;
      }
    } else {
      this.messages = msgs;
    }

    // ✅ Update message list and icon logic
    this.headerService.updateMessagesList.next(this.messages);
    this.messageIcon = 'messages.svg';

    if (this.messages.length) {
      const temp = {
        lastNotificationId: this.messages[0].id,
        cleared: notify.cleared || ''
      };

      if (
        !notify.lastNotificationId ||
        (notify.lastNotificationId && this.messages[0].id !== notify.lastNotificationId)
      ) {
        this.messageIcon = 'messages-new.svg';
      }

      const isError = this.messages.findIndex(
        (item: any) =>
          item.isError ||
          (item.actionableAlert && item.fallAlertStatus === 'TAKE')
      );

      if (isError !== -1) {
        this.messageIcon = 'messages-error.svg';
      }

      this.storageService.setLocalStorage(StorageEnum.notifications, temp, true);
    }
  }

  async openMessageDrawer() {
    const modal = await this.modalCtrl.create({
      component: MessageDrawerComponent,
      cssClass: 'message-drawer-modal',
      componentProps: {
        messages: this.messages,
        lastUpdatedPCC: this.lastUpdatedPCC
      },
      backdropDismiss: false
    });
    await modal.present();
    const { role } = await modal.onWillDismiss();
    // if (role === 'cleared') {
    // }
    this.didGetMessagesData(this.messages);
  }
}
